import {
  BRAZIL_ELECTIONS_RACE,
  BRAZIL_ELECTIONS_RESULTS_SINCE,
  BRAZIL_ELECTIONS_SUBCATEGORY,
  BRAZIL_STATES,
} from '@/constants/brazilElections'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import { getPositionLabels } from '@/features/market/utils/positionLabels'

const PLAY_MONEY_CURRENCY = 'OOM'

/** Price key the market quotes in for the visitor's currency: play money is always OOM. */
export function marketPriceKey(market, currencyId) {
  if (!market) return PLAY_MONEY_CURRENCY
  if (!currencyId || currencyId === PLAY_MONEY_CURRENCY) return PLAY_MONEY_CURRENCY
  return market.canonical_currency || PLAY_MONEY_CURRENCY
}

/** 0–1 probability of an outcome, or null when the market has no quote yet. */
export function outcomeProbability(outcome, market, currencyId) {
  const key = marketPriceKey(market, currencyId)
  const raw = outcome?.price?.[key] ?? outcome?.price?.[PLAY_MONEY_CURRENCY]
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null
}

const TRADE_STAKE = 100

/**
 * Gross return of a $100 ticket at the current quote, rounded to the unit: the
 * price is the probability, so the stake buys `stake / price` shares that each
 * pay 1 if the outcome wins. Ignores fees and the price moving with the order
 * size.
 */
export function payoutFor(probability) {
  return probability > 0 ? Math.round(TRADE_STAKE / probability) : null
}

/** "100ø" in play money, "$100" in real money, matching how the app prints balances. */
export function formatMoney(value, currencyId) {
  const amount = Number(value).toLocaleString('en-US')
  const isPlayMoney = !currencyId || currencyId === PLAY_MONEY_CURRENCY
  return isPlayMoney ? `${amount}ø` : `$${amount}`
}

/** "100ø → 179ø", or null when the outcome has no quote. `i18n` is the bound translator from useI18n(). */
export function payoutLabel(i18n, probability, currencyId) {
  const payout = payoutFor(probability)
  if (!payout) return null
  return i18n('brazil_elections.trade.payout', {
    stake: formatMoney(TRADE_STAKE, currencyId),
    payout: formatMoney(payout, currencyId),
  })
}

/** Markets for a list of ids, in that order, deduplicated (the category reducer appends on every fetch). */
export function marketsFromSlice(ids, pool) {
  const seen = new Set()
  const list = []
  ;(ids || []).forEach(id => {
    const key = String(id)
    if (seen.has(key)) return
    seen.add(key)
    const market = pool?.[id] ?? pool?.[key]
    if (market) list.push(market)
  })
  return list
}

export function formatPercent(probability) {
  if (probability == null) return '—'
  return `${Math.round(probability * 100)}%`
}

/** Outcomes sorted by probability, highest first. Disabled outcomes are dropped. */
export function rankedOutcomes(market, currencyId) {
  const outcomes = Array.isArray(market?.outcomes) ? market.outcomes : []
  return outcomes
    .filter(o => o && !o.disabled)
    .map(o => ({ outcome: o, probability: outcomeProbability(o, market, currencyId) ?? 0 }))
    .sort((a, b) => b.probability - a.probability)
}

export function leadingOutcome(market, currencyId) {
  return rankedOutcomes(market, currencyId)[0] || null
}

/**
 * The configured outcomes of a market, ranked by probability. Falls back to
 * the top of the ranking when any configured id is missing from the payload.
 */
export function pinnedOutcomes(market, outcomeIds, currencyId) {
  const ranked = rankedOutcomes(market, currencyId)
  const ids = Array.isArray(outcomeIds) ? outcomeIds.map(Number) : []
  const pinned = ranked.filter(entry => ids.includes(Number(entry.outcome.id)))
  return pinned.length === ids.length && ids.length > 0 ? pinned : ranked.slice(0, ids.length || 2)
}

/** Yes/no markets name their single outcome after the question, so the leader reads "Yes" instead. */
export function leaderLabel(i18n, market, leader) {
  if (!leader) return ''
  if (market?.outcomes?.length === 1) return i18n('brazil_elections.first_round.yes')
  return leader.outcome.title
}

/** The market whose leader has the highest probability, and the rest in their given order. */
export function splitLeader(markets, currencyId) {
  const list = markets.filter(Boolean)
  if (list.length === 0) return { leader: null, rest: [] }
  const probability = market => leadingOutcome(market, currencyId)?.probability ?? -1
  const leader = list.reduce((best, market) => (probability(market) > probability(best) ? market : best), list[0])
  return { leader, rest: list.filter(market => market !== leader) }
}

/** No longer takes bets: resolved, paused, or past its betting window. */
export function isMarketClosed(market) {
  if (!market) return false
  return market.is_wagerable === false || (market.status != null && market.status !== QUESTION_STATUS.OPEN)
}

/**
 * The winning side of a resolved market, or null while it has none. A
 * multi-outcome market names the winning outcome in `resolution`. A market
 * sold as one outcome carries the winning side on `resolution_display`, read
 * against that outcome's long / short labels as the rest of the app does.
 * `label` is what the page prints: the winner's name, or the side's label
 * (YES / NO localised); `side` is 'long' / 'short' for one-outcome markets.
 */
export function marketResult(i18n, market) {
  if (!market || market.status !== QUESTION_STATUS.RESOLVED) return null
  const outcomes = Array.isArray(market.outcomes) ? market.outcomes : []
  if (outcomes.length > 1) {
    if (market.resolution == null) return null
    const outcome = outcomes.find(o => Number(o?.id) === Number(market.resolution))
    return outcome ? { outcome, label: outcome.title, side: null } : null
  }
  const outcome = outcomes[0]
  const display = outcome?.resolution_display
  if (!display) return null
  const { longLabel, shortLabel } = getPositionLabels(outcome, market)
  const side = display === longLabel ? 'long' : display === shortLabel ? 'short' : null
  if (!side) return null
  const isYesNo = String(longLabel).toLowerCase() === 'yes' || longLabel === i18n('place_bet.yes')
  if (!isYesNo) return { outcome, label: display, side }
  return { outcome, label: i18n(`brazil_elections.first_round.${side === 'long' ? 'yes' : 'no'}`), side }
}

/** Closed without a result to come: cancelled or reversed. */
const isAbandoned = market => market?.status === QUESTION_STATUS.CANCELLED || market?.status === QUESTION_STATUS.REVERSED

/**
 * Keeps the open markets and the ones that closed on election week or later
 * (see BRAZIL_ELECTIONS_RESULTS_SINCE). Campaign side questions that closed
 * earlier, resolved or not, and cancelled / reversed markets stay off.
 */
export function isElectionWeekMarket(market) {
  if (!market) return false
  if (isAbandoned(market)) return false
  if (!isMarketClosed(market)) return true
  const closedAt = Date.parse(market.bet_end_date || '')
  return !Number.isFinite(closedAt) || closedAt >= Date.parse(BRAZIL_ELECTIONS_RESULTS_SINCE)
}

export function marketVolume(market, currencyId) {
  if (!market) return 0
  const isPlayMoney = !currencyId || currencyId === PLAY_MONEY_CURRENCY
  return Number(isPlayMoney ? market.volume_play_money : market.volume_real_money) || 0
}

const categoryIds = market => (Array.isArray(market?.category) ? market.category : []).map(c => Number(c?.id))

/**
 * Race metadata from the market's sub-category (State Governors / Senate) plus
 * the state named in the title ("Elected Governor of Minas Gerais in 2026").
 * `race` is null for markets that are neither, such as the presidential ones.
 * UI labels come from i18n.
 */
export function raceInfo(market) {
  if (!market) return null
  const ids = categoryIds(market)
  const title = String(market.title || '')
  let race = null
  if (ids.includes(BRAZIL_ELECTIONS_SUBCATEGORY.GOVERNORS)) race = BRAZIL_ELECTIONS_RACE.GOVERNOR
  else if (ids.includes(BRAZIL_ELECTIONS_SUBCATEGORY.SENATE)) race = BRAZIL_ELECTIONS_RACE.SENATE

  const stateName =
    Object.keys(BRAZIL_STATES)
      .sort((a, b) => b.length - a.length)
      .find(name => title.toLowerCase().includes(name.toLowerCase())) || null
  return {
    race,
    stateName,
    uf: stateName ? BRAZIL_STATES[stateName] : null,
  }
}

/** Race markets ordered by volume, highest first. */
export function rankRacesByVolume(markets, currencyId) {
  return [...markets]
    .filter(Boolean)
    .sort((a, b) => marketVolume(b, currencyId) - marketVolume(a, currencyId))
}

/** Days / hours / minutes / seconds until `target`; all zeros once it has passed. */
export function countdownParts(target, now = Date.now()) {
  const diff = Math.max(0, new Date(target).getTime() - now)
  const totalSeconds = Math.floor(diff / 1000)
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    finished: diff === 0,
  }
}

export const pad2 = n => String(n).padStart(2, '0')

/** Localised race name for a race code; `i18n` is the bound translator from useI18n(). */
export function raceLabel(i18n, race) {
  if (race === BRAZIL_ELECTIONS_RACE.GOVERNOR) return i18n('brazil_elections.races.governor')
  if (race === BRAZIL_ELECTIONS_RACE.SENATE) return i18n('brazil_elections.races.senate')
  return ''
}
